'use client'

import React, { useState, useEffect, useCallback } from 'react'
import Link from 'next/link'
import { createClient } from '@supabase/supabase-js'
import { PreviewOpportunityCard, PreviewOpportunity } from '@/components/preview/PreviewOpportunityCard'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { getNaicsLabel } from '@/lib/constants/naics-codes'
import { CheckIcon, PlusCircledIcon } from '@radix-ui/react-icons'
import {
  Search,
  Lock,
  Star,
  Sparkles,
  FileText,
  MapPin,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react'

// Public Supabase client (anon key, no auth required)
const supabase = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL!,
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
)

// ── Filter option data (same as dashboard toolbar) ──

const STATE_OPTIONS: { value: string; label: string }[] = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
]

// NAICS_OPTIONS is now fetched from the database at runtime (see dbNaicsOptions state)

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "Presolicitation", label: "Presolicitation" },
  { value: "Solicitation", label: "Solicitation" },
  { value: "Combined Synopsis/Solicitation", label: "Combined Synopsis/Solicitation" },
  { value: "Sources Sought", label: "Sources Sought" },
  { value: "Special Notice", label: "Special Notice" },
  { value: "Award Notice", label: "Award Notice" },
  { value: "Justification", label: "Justification" },
]

const SET_ASIDE_OPTIONS: { value: string; label: string }[] = [
  { value: "8a", label: "8(a)" },
  { value: "SDVOSB", label: "SDVOSB" },
  { value: "WOSB", label: "WOSB" },
  { value: "HUBZone", label: "HUBZone" },
  { value: "Small Business", label: "Small Business" },
]

// ── Grant-specific filter options ──

const GRANT_AGENCY_OPTIONS: { value: string; label: string }[] = [
  { value: 'DOD', label: 'Dept. of Defense' },
  { value: 'HHS', label: 'Health & Human Services' },
  { value: 'NSF', label: 'National Science Foundation' },
  { value: 'DOE', label: 'Dept. of Energy' },
  { value: 'NASA', label: 'NASA' },
  { value: 'USDA', label: 'Dept. of Agriculture' },
  { value: 'DOC', label: 'Dept. of Commerce' },
  { value: 'EPA', label: 'Environmental Protection Agency' },
  { value: 'ED', label: 'Dept. of Education' },
  { value: 'DOT', label: 'Dept. of Transportation' },
  { value: 'DHS', label: 'Dept. of Homeland Security' },
  { value: 'DOI', label: 'Dept. of Interior' },
  { value: 'DOJ', label: 'Dept. of Justice' },
  { value: 'DOL', label: 'Dept. of Labor' },
  { value: 'VA', label: 'Dept. of Veterans Affairs' },
  { value: 'SBA', label: 'Small Business Administration' },
]

const GRANT_CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: 'Discretionary', label: 'Discretionary' },
  { value: 'Mandatory', label: 'Mandatory' },
  { value: 'Continuation', label: 'Continuation' },
  { value: 'Earmark', label: 'Earmark' },
  { value: 'Other', label: 'Other' },
]

const GRANT_FUNDING_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Grant', label: 'Grant' },
  { value: 'Cooperative Agreement', label: 'Cooperative Agreement' },
  { value: 'Procurement Contract', label: 'Procurement Contract' },
  { value: 'Other', label: 'Other' },
]

const GRANT_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'Posted', label: 'Posted / Open' },
  { value: 'Forecasted', label: 'Forecasted' },
  { value: 'Closed', label: 'Closed' },
  { value: 'Archived', label: 'Archived' },
]

const SBIR_PROGRAM_OPTIONS: { value: string; label: string }[] = [
  { value: 'SBIR', label: 'SBIR' },
  { value: 'STTR', label: 'STTR' },
]

const SBIR_PHASE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Phase I', label: 'Phase I' },
  { value: 'Phase II', label: 'Phase II' },
  { value: 'Phase III', label: 'Phase III' },
]

// ── Standalone faceted filter component ──

interface FacetedFilterProps {
  title: string
  options: { value: string; label: string; definition?: string }[]
  selectedValues: Set<string>
  onFilterChange: (values: Set<string>) => void
}

function FacetedFilter({ title, options, selectedValues, onFilterChange }: FacetedFilterProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 border-dashed">
          <PlusCircledIcon className="mr-2 h-4 w-4" />
          {title}
          {selectedValues.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden">
                {selectedValues.size}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                    {selectedValues.size} selected
                  </Badge>
                ) : (
                  options
                    .filter((option) => selectedValues.has(option.value))
                    .map((option) => (
                      <Badge variant="secondary" key={option.value} className="rounded-sm px-1 font-normal">
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              <TooltipProvider delayDuration={300}>
                {options.map((option) => {
                  const isSelected = selectedValues.has(option.value)
                  return (
                    <Tooltip key={option.value}>
                      <TooltipTrigger asChild>
                        <CommandItem
                          onSelect={() => {
                            const next = new Set(selectedValues)
                            if (isSelected) {
                              next.delete(option.value)
                            } else {
                              next.add(option.value)
                            }
                            onFilterChange(next)
                          }}
                        >
                          <div
                            className={cn(
                              "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                              isSelected
                                ? "bg-primary text-primary-foreground"
                                : "opacity-50 [&_svg]:invisible"
                            )}
                          >
                            <CheckIcon className="h-4 w-4" />
                          </div>
                          <span>{option.label} ({option.value})</span>
                        </CommandItem>
                      </TooltipTrigger>
                      {option.definition && (
                        <TooltipContent side="right" align="start" className="max-w-xs">
                          <p className="font-semibold mb-1">{option.label} ({option.value})</p>
                          <p className="text-sm text-muted-foreground">{option.definition}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  )
                })}
              </TooltipProvider>
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => onFilterChange(new Set())}
                    className="justify-center text-center"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// ── Data mapping ──

function mapFederalToPreview(item: any): PreviewOpportunity {
  let value: string | null = null
  let isEstimate = false
  if (item.award_details) {
    try {
      const details = typeof item.award_details === 'string' ? JSON.parse(item.award_details) : item.award_details
      const amount = details?.amount ?? details?.value ?? details?.estimated_value ?? details?.award_amount ?? null
      if (amount !== null && amount !== undefined) {
        const numValue = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.-]/g, '')) : Number(amount)
        if (!isNaN(numValue) && numValue > 0) {
          value = numValue.toString()
        }
      }
    } catch { /* keep null */ }
  }
  if (!value && item.ai_estimated_award) {
    value = String(item.ai_estimated_award)
    isEstimate = true
  }

  const naicsCode = item.primary_naics_code || (item.naics_codes && item.naics_codes.length > 0 ? item.naics_codes[0] : null)

  let agency: string | null = null
  if (item.full_parent_path_name) {
    const parts = item.full_parent_path_name.split('.')
    const lastPart = parts[parts.length - 1]?.trim() || item.full_parent_path_name
    agency = lastPart.length > 50 ? lastPart.substring(0, 47) + '...' : lastPart
  }

  let locationState: string | null = null
  if (item.office_city && item.office_state) locationState = `${item.office_city}, ${item.office_state}`
  else if (item.office_state) locationState = item.office_state
  else if (item.office_city) locationState = item.office_city

  let dueDate: string | null = null
  if (item.response_deadline) {
    try {
      const date = new Date(item.response_deadline)
      if (!isNaN(date.getTime())) dueDate = date.toISOString()
    } catch { /* keep null */ }
  } else if (item.archive_date) {
    try {
      const date = new Date(item.archive_date)
      if (!isNaN(date.getTime())) dueDate = date.toISOString().split('T')[0]
    } catch { /* keep null */ }
  }

  return {
    id: item.notice_id,
    title: item.title,
    agency,
    locationState,
    sourceType: null,
    status: item.type,
    naicsCode,
    value,
    isEstimate,
    dueDate,
    posted_date: item.posted_date,
    aiDescription: item.ai_description,
    category: item.set_aside,
    solicitation_number: item.solicitation_number ?? null,
    setAside: item.set_aside ?? null,
    ui_link: item.ui_link ?? null,
    officeCity: item.office_city ?? null,
    primaryNaicsCode: item.primary_naics_code ?? null,
    classificationCode: item.classification_code ?? null,
    resourceLinks: item.resource_links ?? null,
    noticeLinks: item.notice_links ?? null,
    additionalInfoLink: item.additional_info_link ?? null,
    pointsOfContact: item.points_of_contact ?? null,
  }
}

function mapGrantsGovToPreview(item: any): PreviewOpportunity {
  let value: string | null = null
  if (item.award_ceiling) {
    const num = parseFloat(String(item.award_ceiling).replace(/[^0-9.-]/g, ''))
    if (!isNaN(num) && num > 0) value = num.toString()
  }
  if (!value && item.estimated_funding) {
    const num = parseFloat(String(item.estimated_funding).replace(/[^0-9.-]/g, ''))
    if (!isNaN(num) && num > 0) value = num.toString()
  }

  let dueDate: string | null = null
  if (item.close_date) {
    try {
      const d = new Date(item.close_date)
      if (!isNaN(d.getTime())) dueDate = d.toISOString()
    } catch {}
  }

  const grantUrl = item.additional_info_url || 
    (item.opportunity_number ? `https://www.grants.gov/search-results-detail/${item.opportunity_id || item.opportunity_number}` : null)

  return {
    id: `gg-${item.id}`,
    title: item.title || 'Untitled Grant',
    agency: item.agency_name || item.agency || null,
    locationState: null,
    sourceType: null,
    status: item.opportunity_status || null,
    naicsCode: null,
    value,
    isEstimate: false,
    dueDate,
    posted_date: item.posted_date,
    aiDescription: item.description ? (item.description.length > 300 ? item.description.substring(0, 300) + '...' : item.description) : null,
    description: item.description || null,
    category: item.category_of_funding || null,
    solicitation_number: item.opportunity_number || null,
    ui_link: grantUrl,
    fundingType: item.funding_instrument_type || null,
    awardCeiling: item.award_ceiling || null,
    awardFloor: item.award_floor || null,
    expectedAwards: item.expected_awards || null,
    costSharing: item.cost_sharing || null,
    eligibleApplicants: item.eligible_applicants || null,
    cfdaNumbers: item.cfda_numbers || null,
    grantSource: 'grants.gov',
    grantorContactName: item.grantor_contact_name || null,
    grantorContactEmail: item.grantor_contact_email || null,
    grantorContactPhone: item.grantor_contact_phone || null,
  }
}

function mapSbirToPreview(item: any): PreviewOpportunity {
  let dueDate: string | null = null
  const rawDate = item.close_date || item.primary_due_date
  if (rawDate) {
    try {
      const d = new Date(rawDate)
      if (!isNaN(d.getTime())) dueDate = d.toISOString()
    } catch {}
  }

  const topicUrl = item.topic_link || item.solicitation_agency_url || null

  return {
    id: `sbir-${item.id}`,
    title: item.topic_title || item.solicitation_title || 'Untitled SBIR/STTR',
    agency: item.agency || null,
    locationState: null,
    sourceType: null,
    status: item.current_status || null,
    naicsCode: null,
    value: null,
    isEstimate: false,
    dueDate,
    posted_date: item.open_date || item.release_date || null,
    aiDescription: item.topic_description ? (item.topic_description.length > 300 ? item.topic_description.substring(0, 300) + '...' : item.topic_description) : null,
    description: item.topic_description || null,
    category: item.program || null,
    solicitation_number: item.solicitation_number || null,
    ui_link: topicUrl,
    grantSource: 'sbir.gov',
    phase: item.phase || null,
    program: item.program || null,
    topicNumber: item.topic_number || null,
    topicTitle: item.topic_title || null,
    topicDescription: item.topic_description || null,
  }
}

// ── Page component ──

export default function ActiveOpportunitiesPage() {
  // Data state
  const [opportunities, setOpportunities] = useState<PreviewOpportunity[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)

  // UI / pagination state
  const [contractLevel, setContractLevel] = useState<'federal' | 'sled' | 'grants'>('federal')
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  // Filter state (multi-select sets) — federal
  const [locationFilter, setLocationFilter] = useState<Set<string>>(new Set())
  const [naicsFilter, setNaicsFilter] = useState<Set<string>>(new Set())
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set())
  const [setAsideFilter, setSetAsideFilter] = useState<Set<string>>(new Set())

  // Filter state — grants
  const [grantSource, setGrantSource] = useState<'all' | 'grants_gov' | 'sbir'>('all')
  const [grantAgencyFilter, setGrantAgencyFilter] = useState<Set<string>>(new Set())
  const [grantCategoryFilter, setGrantCategoryFilter] = useState<Set<string>>(new Set())
  const [grantFundingTypeFilter, setGrantFundingTypeFilter] = useState<Set<string>>(new Set())
  const [grantStatusFilter, setGrantStatusFilter] = useState<Set<string>>(new Set())
  const [sbirProgramFilter, setSbirProgramFilter] = useState<Set<string>>(new Set())
  const [sbirPhaseFilter, setSbirPhaseFilter] = useState<Set<string>>(new Set())

  // NAICS filter options fetched from DB
  const [dbNaicsOptions, setDbNaicsOptions] = useState<{ value: string; label: string }[]>([])

  useEffect(() => {
    if (contractLevel !== 'federal') return;
    const fetchNaicsCodes = async () => {
      try {
        const { data, error } = await supabase
          .from('samgov')
          .select('naics_codes')
          .not('naics_codes', 'is', null);
        if (error || !data) return;
        const codeSet = new Set<string>();
        for (const row of data) {
          if (Array.isArray(row.naics_codes)) {
            for (const code of row.naics_codes) {
              if (code) codeSet.add(code);
            }
          }
        }
        setDbNaicsOptions(
          Array.from(codeSet)
            .map(code => ({ value: code, label: getNaicsLabel(code) }))
            .sort((a, b) => a.label.localeCompare(b.label))
        );
      } catch {}
    };
    fetchNaicsCodes();
  }, [contractLevel]);

  // Modal state
  const [isSledModalOpen, setIsSledModalOpen] = useState(false)

  // Derived: are any filters active?
  const isFiltered = contractLevel === 'grants'
    ? (searchQuery.length > 0 || grantAgencyFilter.size > 0 || grantCategoryFilter.size > 0 ||
       grantFundingTypeFilter.size > 0 || grantStatusFilter.size > 0 || sbirProgramFilter.size > 0 || sbirPhaseFilter.size > 0)
    : (searchQuery.length > 0 || locationFilter.size > 0 || naicsFilter.size > 0 || statusFilter.size > 0 || setAsideFilter.size > 0)

  const clearAllFilters = useCallback(() => {
    setSearchInput('')
    setSearchQuery('')
    setLocationFilter(new Set())
    setNaicsFilter(new Set())
    setStatusFilter(new Set())
    setSetAsideFilter(new Set())
    setGrantAgencyFilter(new Set())
    setGrantCategoryFilter(new Set())
    setGrantFundingTypeFilter(new Set())
    setGrantStatusFilter(new Set())
    setSbirProgramFilter(new Set())
    setSbirPhaseFilter(new Set())
    setPageIndex(0)
  }, [])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput)
      setPageIndex(0)
    }, 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Reset pagination when any filter changes
  useEffect(() => {
    setPageIndex(0)
  }, [locationFilter, naicsFilter, statusFilter, setAsideFilter, grantAgencyFilter, grantCategoryFilter, grantFundingTypeFilter, grantStatusFilter, sbirProgramFilter, sbirPhaseFilter, grantSource])

  // Clear state on tab switch
  useEffect(() => {
    setOpportunities([])
    setTotalCount(0)
    setPageIndex(0)
    clearAllFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractLevel])

  // Fetch opportunities based on active tab
  useEffect(() => {
    if (contractLevel === 'sled') return

    const sanitizeForPostgrest = (term: string): string => term.replace(/[,()."'\\]/g, ' ').trim()
    const searchWords = searchQuery
      ? sanitizeForPostgrest(searchQuery).split(/\s+/).filter(w => w.length >= 2)
      : []

    const fetchFederalOpportunities = async () => {
      setIsLoading(true)
      setFetchError(null)
      const rangeFrom = pageIndex * pageSize
      const rangeTo = rangeFrom + pageSize - 1

      try {
        const selectColumns = 'notice_id, title, solicitation_number, posted_date, response_deadline, archive_date, ui_link, naics_codes, full_parent_path_name, type, set_aside, office_state, award_details, office_city, primary_naics_code, classification_code, ai_estimated_award, ai_description, resource_links, notice_links, additional_info_link, points_of_contact'
        let query = supabase
          .from('samgov')
          .select(selectColumns, { count: 'exact' })
          .range(rangeFrom, rangeTo)

        for (const word of searchWords) {
          query = query.or(`title.ilike.%${word}%,full_parent_path_name.ilike.%${word}%,ai_description.ilike.%${word}%`)
        }
        if (locationFilter.size > 0) query = query.in('office_state', Array.from(locationFilter))
        if (naicsFilter.size > 0) {
          const orConditions = Array.from(naicsFilter).map((code) => `naics_codes.cs.{${code}}`).join(',')
          query = query.or(orConditions)
        }
        if (statusFilter.size > 0) query = query.in('type', Array.from(statusFilter))
        if (setAsideFilter.size > 0) query = query.in('set_aside', Array.from(setAsideFilter))
        query = query.order('posted_date', { ascending: false })

        const { data: fetchedData, error, count } = await query
        if (error) throw new Error(error.message)
        setOpportunities(fetchedData ? fetchedData.map(mapFederalToPreview) : [])
        setTotalCount(count ?? 0)
      } catch (error: any) {
        console.error('Error fetching opportunities:', error)
        setFetchError(error.message || 'Failed to load opportunities.')
        setOpportunities([])
        setTotalCount(0)
      } finally {
        setIsLoading(false)
      }
    }

    const fetchGrantOpportunities = async () => {
      setIsLoading(true)
      setFetchError(null)
      const rangeFrom = pageIndex * pageSize
      const rangeTo = rangeFrom + pageSize - 1

      try {
        let allResults: PreviewOpportunity[] = []
        let combinedTotal = 0

        const fetchGrants = grantSource === 'all' || grantSource === 'grants_gov'
        const fetchSbir = grantSource === 'all' || grantSource === 'sbir'

        if (fetchGrants) {
          const selectCols = 'id, opportunity_number, opportunity_id, title, agency, agency_name, opportunity_status, posted_date, close_date, archive_date, description, cfda_numbers, funding_instrument_type, category_of_funding, expected_awards, estimated_funding, award_ceiling, award_floor, eligible_applicants, additional_info_url, grantor_contact_email, grantor_contact_name, grantor_contact_phone, cost_sharing, opportunity_category'
          let gQuery = supabase
            .from('grants_gov')
            .select(selectCols, { count: 'exact' })

          for (const word of searchWords) {
            gQuery = gQuery.or(`title.ilike.%${word}%,agency_name.ilike.%${word}%,description.ilike.%${word}%`)
          }
          if (grantAgencyFilter.size > 0) gQuery = gQuery.in('agency', Array.from(grantAgencyFilter))
          if (grantCategoryFilter.size > 0) gQuery = gQuery.in('category_of_funding', Array.from(grantCategoryFilter))
          if (grantFundingTypeFilter.size > 0) gQuery = gQuery.in('funding_instrument_type', Array.from(grantFundingTypeFilter))
          if (grantStatusFilter.size > 0) gQuery = gQuery.in('opportunity_status', Array.from(grantStatusFilter))
          gQuery = gQuery.order('posted_date', { ascending: false })

          if (grantSource === 'grants_gov') {
            gQuery = gQuery.range(rangeFrom, rangeTo)
          }

          const { data: gData, error: gErr, count: gCount } = await gQuery
          if (gErr) throw new Error(gErr.message)
          if (gData) allResults.push(...gData.map(mapGrantsGovToPreview))
          combinedTotal += gCount ?? 0
        }

        if (fetchSbir) {
          const selectCols = 'id, solicitation_id, solicitation_number, solicitation_title, topic_number, topic_title, topic_description, agency, branch, program, phase, solicitation_year, open_date, close_date, release_date, primary_due_date, current_status, solicitation_agency_url, topic_link'
          let sQuery = supabase
            .from('sbir_solicitations')
            .select(selectCols, { count: 'exact' })

          for (const word of searchWords) {
            sQuery = sQuery.or(`topic_title.ilike.%${word}%,solicitation_title.ilike.%${word}%,agency.ilike.%${word}%,topic_description.ilike.%${word}%`)
          }
          if (grantAgencyFilter.size > 0) sQuery = sQuery.in('agency', Array.from(grantAgencyFilter))
          if (sbirProgramFilter.size > 0) sQuery = sQuery.in('program', Array.from(sbirProgramFilter))
          if (sbirPhaseFilter.size > 0) sQuery = sQuery.in('phase', Array.from(sbirPhaseFilter))
          if (grantStatusFilter.size > 0) sQuery = sQuery.in('current_status', Array.from(grantStatusFilter))
          sQuery = sQuery.order('open_date', { ascending: false })

          if (grantSource === 'sbir') {
            sQuery = sQuery.range(rangeFrom, rangeTo)
          }

          const { data: sData, error: sErr, count: sCount } = await sQuery
          if (sErr) throw new Error(sErr.message)
          if (sData) allResults.push(...sData.map(mapSbirToPreview))
          combinedTotal += sCount ?? 0
        }

        if (grantSource === 'all') {
          allResults.sort((a, b) => {
            const da = a.posted_date ? new Date(a.posted_date).getTime() : 0
            const db = b.posted_date ? new Date(b.posted_date).getTime() : 0
            return db - da
          })
          setOpportunities(allResults.slice(rangeFrom, rangeTo + 1))
        } else {
          setOpportunities(allResults)
        }
        setTotalCount(combinedTotal)
      } catch (error: any) {
        console.error('Error fetching grants:', error)
        setFetchError(error.message || 'Failed to load grants.')
        setOpportunities([])
        setTotalCount(0)
      } finally {
        setIsLoading(false)
      }
    }

    if (contractLevel === 'federal') {
      fetchFederalOpportunities()
    } else if (contractLevel === 'grants') {
      fetchGrantOpportunities()
    }
  }, [contractLevel, pageIndex, pageSize, searchQuery, locationFilter, naicsFilter, statusFilter, setAsideFilter, grantSource, grantAgencyFilter, grantCategoryFilter, grantFundingTypeFilter, grantStatusFilter, sbirProgramFilter, sbirPhaseFilter])

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  const handleSledToggleClick = useCallback(() => {
    setIsSledModalOpen(true)
  }, [])

  return (
    <div className="container max-w-7xl mx-auto px-4 py-12 dashboard-cards">
      {/* Hero Section */}
      <div className="mb-8">
        <h1 className="text-3xl lg:text-4xl font-bold">
          {contractLevel === 'grants' ? 'Federal Grant Opportunities' : 'Federal Contract Opportunities'}
        </h1>
        <div className="flex items-center gap-3 mt-2">
          <p className="text-muted-foreground">
            {contractLevel === 'grants' ? 'Grants and SBIR/STTR solicitations sourced daily' : 'Open opportunities sourced daily from across the nation'}
          </p>
          {totalCount > 0 && (
            <Badge className="bg-primary/10 text-primary border border-primary/20 hover:bg-primary/15 px-3 py-1 text-sm font-semibold tabular-nums">
              <FileText className="h-3.5 w-3.5 mr-1.5" />
              {totalCount.toLocaleString()}+ active
            </Badge>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 py-3 px-1 flex-wrap mb-4">
        {/* Federal/SLED/Grants Toggle */}
        <ToggleGroup
          type="single"
          value={contractLevel}
          onValueChange={(value) => {
            if (value === 'sled') {
              handleSledToggleClick()
              return
            }
            if (value === 'federal' || value === 'grants') {
              setContractLevel(value)
            }
          }}
          aria-label="Contract Level"
          className="h-9"
        >
          <ToggleGroupItem value="federal" aria-label="Federal Only" className="px-3 h-9 text-sm">
            Federal
          </ToggleGroupItem>
          <ToggleGroupItem
            value="sled"
            aria-label="SLED Only"
            className={cn('px-3 h-9 text-sm', 'opacity-60 cursor-pointer')}
          >
            SLED
            <Lock className="ml-1 h-3 w-3 text-muted-foreground" />
          </ToggleGroupItem>
          <ToggleGroupItem value="grants" aria-label="Grants" className="px-3 h-9 text-sm">
            Grants
          </ToggleGroupItem>
        </ToggleGroup>

        {/* Grant Source Sub-filter */}
        {contractLevel === 'grants' && (
          <ToggleGroup
            type="single"
            value={grantSource}
            onValueChange={(value) => {
              if (value === 'all' || value === 'grants_gov' || value === 'sbir') {
                setGrantSource(value)
              }
            }}
            className="h-9"
          >
            <ToggleGroupItem value="all" className="px-2.5 h-8 text-xs">All</ToggleGroupItem>
            <ToggleGroupItem value="grants_gov" className="px-2.5 h-8 text-xs">Grants.gov</ToggleGroupItem>
            <ToggleGroupItem value="sbir" className="px-2.5 h-8 text-xs">SBIR/STTR</ToggleGroupItem>
          </ToggleGroup>
        )}

        {/* Divider */}
        <div className="h-6 w-px bg-border mx-1" />

        {/* Search Input */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by keyword..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="h-9 pl-9 pr-3 text-sm bg-background border-border focus:border-primary focus:ring-1 focus:ring-primary transition-all"
          />
        </div>

        {/* Faceted Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {contractLevel === 'federal' && (
            <>
              <FacetedFilter title="Location" options={STATE_OPTIONS} selectedValues={locationFilter} onFilterChange={setLocationFilter} />
              <FacetedFilter title="NAICS" options={dbNaicsOptions} selectedValues={naicsFilter} onFilterChange={setNaicsFilter} />
              <FacetedFilter title="Status" options={STATUS_OPTIONS} selectedValues={statusFilter} onFilterChange={setStatusFilter} />
              <FacetedFilter title="Set Aside" options={SET_ASIDE_OPTIONS} selectedValues={setAsideFilter} onFilterChange={setSetAsideFilter} />
            </>
          )}
          {contractLevel === 'grants' && (
            <>
              <FacetedFilter title="Agency" options={GRANT_AGENCY_OPTIONS} selectedValues={grantAgencyFilter} onFilterChange={setGrantAgencyFilter} />
              <FacetedFilter title="Status" options={GRANT_STATUS_OPTIONS} selectedValues={grantStatusFilter} onFilterChange={setGrantStatusFilter} />
              {(grantSource === 'all' || grantSource === 'grants_gov') && (
                <>
                  <FacetedFilter title="Category" options={GRANT_CATEGORY_OPTIONS} selectedValues={grantCategoryFilter} onFilterChange={setGrantCategoryFilter} />
                  <FacetedFilter title="Funding Type" options={GRANT_FUNDING_TYPE_OPTIONS} selectedValues={grantFundingTypeFilter} onFilterChange={setGrantFundingTypeFilter} />
                </>
              )}
              {(grantSource === 'all' || grantSource === 'sbir') && (
                <>
                  <FacetedFilter title="Program" options={SBIR_PROGRAM_OPTIONS} selectedValues={sbirProgramFilter} onFilterChange={setSbirProgramFilter} />
                  <FacetedFilter title="Phase" options={SBIR_PHASE_OPTIONS} selectedValues={sbirPhaseFilter} onFilterChange={setSbirPhaseFilter} />
                </>
              )}
            </>
          )}

          {/* Clear All */}
          {isFiltered && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="h-9 px-3 text-muted-foreground hover:text-foreground hover:bg-muted/50"
            >
              <X className="mr-1.5 h-3.5 w-3.5" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950/20 dark:to-cyan-950/20 border border-blue-200 dark:border-blue-800/40 rounded-lg p-4 shadow-sm mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm text-blue-900 dark:text-blue-100 font-medium mb-1">
              {contractLevel === 'grants' ? (
                <><strong>Explore Federal Grants:</strong> Browse Grants.gov and SBIR/STTR solicitations. Sign up for a free trial to unlock tracking, AI-powered analysis, and matched opportunities.</>
              ) : (
                <><strong>Explore Federal Opportunities:</strong> Sign up for a free 14-day trial to unlock SLED (State, Local, and Education) opportunities, tracking, and AI-powered proposal generation.</>
              )}
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-200/80">
              No credit card required. Get full access to all features during your trial.
            </p>
          </div>
          <Button
            asChild
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-700 dark:hover:bg-blue-600 whitespace-nowrap gap-2"
          >
            <Link href="/signup">
              Start Free Trial
            </Link>
          </Button>
        </div>
      </div>

      {/* Pagination Controls - Top */}
      <div className="flex items-center justify-between space-x-2 py-2 mb-4">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Items per page</p>
          <Select
            value={`${pageSize}`}
            onValueChange={(value) => {
              setPageSize(Number(value))
              setPageIndex(0)
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={pageSize} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPageIndex(Math.max(0, pageIndex - 1))
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
            disabled={pageIndex === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm font-medium">
            Page {pageIndex + 1} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPageIndex(Math.min(totalPages - 1, pageIndex + 1))
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
            disabled={pageIndex >= totalPages - 1}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="mb-8">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="h-64">
                <CardHeader>
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2 mt-2" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : fetchError ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Error: {fetchError}</p>
          </div>
        ) : opportunities.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              No {contractLevel === 'grants' ? 'grant' : 'federal'} opportunities found matching your criteria.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {opportunities.map((opportunity) => (
              <PreviewOpportunityCard
                key={opportunity.id}
                opportunity={opportunity}
                contractLevel={contractLevel}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pagination Controls - Bottom */}
      {!isLoading && opportunities.length > 0 && (
        <div className="flex items-center justify-between space-x-2 py-4 mb-8">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Items per page</p>
            <Select
              value={`${pageSize}`}
              onValueChange={(value) => {
                setPageSize(Number(value))
                setPageIndex(0)
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPageIndex(Math.max(0, pageIndex - 1))
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
              disabled={pageIndex === 0}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm font-medium">
              Page {pageIndex + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPageIndex(Math.min(totalPages - 1, pageIndex + 1))
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
              disabled={pageIndex >= totalPages - 1}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className="text-center p-8 border border-dashed rounded-lg bg-muted/30">
        <div className="flex justify-center mb-4">
          <div className="p-3 rounded-full bg-primary/10">
            <Sparkles className="h-8 w-8 text-primary" />
          </div>
        </div>
        <h2 className="text-2xl font-semibold mb-2 text-foreground">
          Start Your Free 14-Day Trial
        </h2>
        <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
          Get full access to {totalCount > 0 ? `${totalCount.toLocaleString()}+` : 'thousands of'} federal contracts, SLED opportunities across all 50 states,
          AI-powered proposal generation, opportunity tracking, and advanced analytics. No credit card required.
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <Button asChild size="lg">
            <Link href="/signup">Start Free Trial</Link>
          </Button>
          <Button variant="outline" size="lg" asChild>
            <Link href="/login">Already have an account? Log In</Link>
          </Button>
        </div>
      </div>

      {/* SLED Upgrade Modal */}
      <Dialog open={isSledModalOpen} onOpenChange={setIsSledModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-full bg-primary/10">
                <Lock className="h-8 w-8 text-primary" />
              </div>
            </div>
            <DialogTitle className="text-center text-xl">
              Start Your Free 14-Day Trial
            </DialogTitle>
            <DialogDescription className="text-center">
              Unlock SLED (State, Local, and Education) contract opportunities across all 50 states. No credit card required.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <h5 className="font-semibold text-sm">With your free trial, you&apos;ll get:</h5>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary shrink-0" />
                  AI-powered proposal generation
                </li>
                <li className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-primary shrink-0" />
                  Track & save opportunities
                </li>
                <li className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary shrink-0" />
                  Full opportunity details & documents
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary shrink-0" />
                  Access to SLED opportunities in all 50 states
                </li>
              </ul>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <Button asChild size="lg" className="w-full">
                <Link href="/signup">
                  Start Free Trial
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm" className="w-full">
                <Link href="/login">
                  Already have an account? Log in
                </Link>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
